import { memo, useEffect, useState } from 'react';

import { InfoCircledIcon } from '@radix-ui/react-icons';
import {
  Tooltip as ApsaraToolTip,
  Flex,
  IconButton,
  Table,
  Text
} from '@raystack/apsara';
import * as _ from 'lodash';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import * as Skeleton from '@src/components/PixxelUIKit/Skeleton';
import {
  CROPS_STRESS_AREA_INFO,
  CROP_STRESS_GRAPH_OUTPUT_NAME
} from '@src/utils/constants/MODE_INFERENCE_DEFAULTS';
import { INPUT_FORMATS } from '@src/utils/constants/WORKFLOW_CONSTANTS';
import { fetchTabData } from '@src/utils/helpers/WorkflowHelpers';
import { getCogURLForOutput } from '@src/utils/workFlowUtils';

import styles from './styles.module.css';

export const getCropStressOutputTilerUrl = (outputs, format) => {
  // generate render params
  const cropStressRasterOutput = outputs.find(
    i => i.format === INPUT_FORMATS.FormatRaster
  );

  if (!cropStressRasterOutput) return '';

  return getCogURLForOutput(cropStressRasterOutput, format);
};

export const StressScoreGraph = ({ data }) => {
  return (
    <>
      <BarChart
        width={370}
        height={350}
        data={data}
        margin={{
          top: 0,
          right: 0,
          left: -10
        }}
      >
        <XAxis dataKey='Stress (Cloud free crop area)' fontSize={11} />
        <YAxis
          fontSize={12}
          dataKey='Crop Area Coverage (%)'
          label={{
            value: 'Crop Area Coverage (%)',
            style: { textAnchor: 'middle', fontSize: '11px' },
            angle: -90,
            position: 'left',
            offset: -18
          }}
        />
        <CartesianGrid
          strokeDasharray='3 3'
          stroke='var(--rs-color-foreground-base-secondary)'
          vertical={false}
          opacity={0.4}
        />
        <Tooltip
          itemStyle={{
            color: 'var(--rs-color-foreground-base-primary)',
            fontSize: '11px'
          }}
          labelStyle={{
            color: 'var(--rs-color-background-accent-emphasis-hover)',
            fontSize: '14px'
          }}
          contentStyle={{
            backgroundColor: 'var(--rs-color-background-base-primary)',
            borderRadius: 'var(--rs-radius-2)',
            border: `1px solid var(--rs-color-border-base-secondary)`,
            padding: '8px',
            outline: 'none'
          }}
        />
        <Bar dataKey='Crop Area Coverage (%)'>
          {data.map((bar, index) => (
            <Cell key={index} fill={bar?.['Colour Code']} />
          ))}
        </Bar>
      </BarChart>
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.Head></Table.Head>
            <Table.Head>Stress Level</Table.Head>
            <Table.Head>Area</Table.Head>
            <Table.Head>Crop Area Coverage</Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell>
                <div
                  className={styles.filledBox}
                  style={{
                    backgroundColor: `${item?.['Colour Code']}`
                  }}
                />
              </Table.Cell>
              <Table.Cell>{item['Stress (Cloud free crop area)']}</Table.Cell>
              <Table.Cell>
                {item['Area (Sq KM)']} km<sup>2</sup>
              </Table.Cell>
              <Table.Cell>{item['Crop Area Coverage']} %</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </>
  );
};

export const CropStressCommon = memo(({ outputs, activeAoi = null }) => {
  const [tabData, setTabData] = useState({});
  const [activeTabData, setActiveTabData] = useState([]);
  const [loader, setLoader] = useState(false);

  // fetch csv data
  useEffect(() => {
    if (outputs) {
      fetchTabData(outputs, setTabData, value => setLoader(value), activeAoi);
    }
  }, [outputs, activeAoi]);

  //set activeGraph
  useEffect(() => {
    if (!_.isEmpty(tabData)) {
      const tableData = [
        ...tabData[CROP_STRESS_GRAPH_OUTPUT_NAME].map(item => {
          const data = {
            ...item,
            'Area (Sq KM)': Number(item['Crop Area (Sq KM)']),
            'Crop Area Coverage (%)': Number(item['Crop Area Coverage']),
            'Stress value': Number(item['Stress value'])
          };
          return data;
        })
      ];
      setActiveTabData(tableData);
    }
  }, [tabData]);
  return (
    <>
      {/* Stress Score Section */}
      <Flex direction='column' gap='medium' align='start'>
        <Flex align='center' gap='small'>
          <Text size={4} weight={500} variant='primary'>
            Stress Score
          </Text>
          <ApsaraToolTip message={CROPS_STRESS_AREA_INFO} side='top'>
            <IconButton size={3} data-test-id='crop-stress-output-info-btn'>
              <InfoCircledIcon />
            </IconButton>
          </ApsaraToolTip>
        </Flex>
      </Flex>

      {loader ? (
        <Skeleton.TileList />
      ) : (
        <>
          {activeTabData && activeTabData.length > 0 && (
            <StressScoreGraph data={activeTabData.slice(0, -1)} />
          )}
        </>
      )}
    </>
  );
});

CropStressCommon.displayName = 'CropStressCommon';
